import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Plus, Trash2, HelpCircle, Edit, Copy, Image as ImageIcon, X, Eye } from 'lucide-react';
import TicketTypeExamples from './TicketTypeExamples';
import TicketPreview from './TicketPreview';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketTypesManager({ eventId }) {
  const { tenant } = useAuth();
  const [ticketTypes, setTicketTypes] = useState([]);
  const [zones, setZones] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    quantity_available: '',
    selectedZones: [],
    qr_color: '#000000',
    qr_bg_color: '#FFFFFF',
    qr_include_logo: false,
    qr_style: 'standard',
    require_photo: false,
    custom_image: null,
    custom_image_position: 'top-right',
    custom_image_opacity: 100
  });
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [ticketTypesRes, zonesRes] = await Promise.all([
        axios.get(`${API_URL}/events/${eventId}/ticket-types`),
        axios.get(`${API_URL}/events/${eventId}/access-zones`)
      ]);
      setTicketTypes(ticketTypesRes.data);
      setZones(zonesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      description: '', 
      price: '', 
      quantity_available: '', 
      selectedZones: [],
      qr_color: '#000000',
      qr_bg_color: '#FFFFFF',
      qr_include_logo: false,
      qr_style: 'standard',
      require_photo: false,
      custom_image: null,
      custom_image_position: 'top-right',
      custom_image_opacity: 100
    });
    setImagePreview(null);
    setEditingId(null);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Immagine troppo grande! Massimo 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setFormData({...formData, custom_image: base64});
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setFormData({...formData, custom_image: null});
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        quantity_available: parseInt(formData.quantity_available),
        qr_color: formData.qr_color,
        qr_bg_color: formData.qr_bg_color,
        qr_include_logo: formData.qr_include_logo,
        qr_style: formData.qr_style,
        require_photo: formData.require_photo,
        custom_image: formData.custom_image,
        custom_image_position: formData.custom_image_position,
        custom_image_opacity: formData.custom_image_opacity
      };

      if (editingId) {
        // Update
        await axios.put(`${API_URL}/ticket-types/${editingId}`, payload);
        toast.success('Tipologia aggiornata!');
      } else {
        // Create
        const response = await axios.post(`${API_URL}/events/${eventId}/ticket-types`, payload);
        
        // Associate with zones
        for (const zoneId of formData.selectedZones) {
          await axios.post(`${API_URL}/ticket-type-zones?ticket_type_id=${response.data.id}&access_zone_id=${zoneId}`);
        }
        toast.success('Tipologia creata!');
      }

      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('biglietti già venduti')) {
        toast.error('Impossibile modificare: ci sono biglietti già venduti o assegnati');
      } else {
        toast.error('Errore durante il salvataggio');
      }
    }
  };

  const handleEdit = async (ticketType) => {
    try {
      // Check if can edit
      const canEditRes = await axios.get(`${API_URL}/ticket-types/${ticketType.id}/can-edit`);
      
      if (canEditRes.data.can_edit) {
        // Can edit directly
        setEditingId(ticketType.id);
        setFormData({
          name: ticketType.name,
          description: ticketType.description,
          price: ticketType.price.toString(),
          quantity_available: ticketType.quantity_available.toString(),
          selectedZones: [], // TODO: fetch current zones
          qr_color: ticketType.qr_color || '#000000',
          qr_bg_color: ticketType.qr_bg_color || '#FFFFFF',
          qr_include_logo: ticketType.qr_include_logo || false,
          qr_style: ticketType.qr_style || 'standard'
        });
        setShowForm(true);
      } else {
        // Cannot edit, show duplicate dialog
        setDuplicating(ticketType);
        setShowDuplicateDialog(true);
      }
    } catch (error) {
      toast.error('Errore durante il caricamento');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await axios.post(`${API_URL}/ticket-types/${duplicating.id}/duplicate`);
      toast.success('Copia creata! Ora puoi modificarla.');
      setShowDuplicateDialog(false);
      setDuplicating(null);
      fetchData();
      // Open edit for the new copy
      setTimeout(() => handleEdit(response.data), 500);
    } catch (error) {
      toast.error('Errore durante la duplicazione');
    }
  };

  const handleDelete = async (ticketTypeId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa tipologia?')) return;
    try {
      await axios.delete(`${API_URL}/ticket-types/${ticketTypeId}`);
      toast.success('Tipologia eliminata');
      fetchData();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('biglietti già venduti')) {
        toast.error('Impossibile eliminare: ci sono biglietti già venduti o assegnati');
      } else {
        toast.error('Errore durante l\'eliminazione');
      }
    }
  };

  const toggleZone = (zoneId) => {
    setFormData(prev => ({
      ...prev,
      selectedZones: prev.selectedZones.includes(zoneId)
        ? prev.selectedZones.filter(id => id !== zoneId)
        : [...prev.selectedZones, zoneId]
    }));
  };

  return (
    <div className="space-y-6" data-testid="ticket-types-manager">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tipologie Biglietti</h2>
          <p className="text-gray-600 mt-1">
            Crea tutte le tipologie che vuoi (Standard, VIP, Premium, ecc.) ognuna con prezzo, accessi e QR personalizzati
          </p>
          {ticketTypes.length > 0 && (
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {ticketTypes.length} {ticketTypes.length === 1 ? 'tipologia creata' : 'tipologie create'}
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="show-examples-button">
                <HelpCircle className="w-4 h-4 mr-2" />
                Vedi Esempi
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Esempi di Tipologie</DialogTitle>
              </DialogHeader>
              <TicketTypeExamples />
            </DialogContent>
          </Dialog>
          <Button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-ticket-type-button">
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Tipologia
          </Button>
        </div>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Modifica Tipologia Biglietto' : 'Nuova Tipologia Biglietto'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900">
                  💡 <strong>Suggerimento:</strong> Puoi creare tutte le tipologie che vuoi! Es: "Ingresso Base", "VIP Backstage", "Premium All-Inclusive", "Gratuito Staff", ecc.
                </p>
              </div>
              
              <div>
                <Label htmlFor="name">Nome Tipologia *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="ticket-type-name-input"
                  placeholder="Es: Standard, VIP, Premium, Backstage..."
                  className="text-lg font-semibold"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Scegli un nome che descriva questa tipologia di biglietto
                </p>
              </div>
              <div>
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="ticket-type-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Prezzo (€) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                    data-testid="ticket-type-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="quantity">Quantità Disponibile *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={formData.quantity_available}
                    onChange={(e) => setFormData({...formData, quantity_available: e.target.value})}
                    required
                    data-testid="ticket-type-quantity-input"
                  />
                </div>
              </div>
              
              {zones.length > 0 && !editingId && (
                <div>
                  <Label>Zone di Accesso</Label>
                  <div className="mt-2 space-y-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`zone-${zone.id}`}
                          checked={formData.selectedZones.includes(zone.id)}
                          onCheckedChange={() => toggleZone(zone.id)}
                          data-testid={`zone-checkbox-${zone.id}`}
                        />
                        <label
                          htmlFor={`zone-${zone.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {zone.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Personalizzazione QR Code */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="font-semibold text-lg mb-4">Personalizzazione QR Code</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="qr_color">Colore QR</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="qr_color"
                        type="color"
                        value={formData.qr_color}
                        onChange={(e) => setFormData({...formData, qr_color: e.target.value})}
                        className="w-16 h-10"
                        data-testid="qr-color-input"
                      />
                      <Input
                        type="text"
                        value={formData.qr_color}
                        onChange={(e) => setFormData({...formData, qr_color: e.target.value})}
                        className="flex-1"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="qr_bg_color">Colore Sfondo</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="qr_bg_color"
                        type="color"
                        value={formData.qr_bg_color}
                        onChange={(e) => setFormData({...formData, qr_bg_color: e.target.value})}
                        className="w-16 h-10"
                        data-testid="qr-bg-color-input"
                      />
                      <Input
                        type="text"
                        value={formData.qr_bg_color}
                        onChange={(e) => setFormData({...formData, qr_bg_color: e.target.value})}
                        className="flex-1"
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center space-x-2">
                  <Checkbox
                    id="qr_include_logo"
                    checked={formData.qr_include_logo}
                    onCheckedChange={(checked) => setFormData({...formData, qr_include_logo: checked})}
                    data-testid="qr-include-logo-checkbox"
                  />
                  <label
                    htmlFor="qr_include_logo"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Includi logo aziendale al centro del QR Code
                  </label>
                </div>

                <div className="mt-4 flex items-center space-x-2">
                  <Checkbox
                    id="require_photo"
                    checked={formData.require_photo}
                    onCheckedChange={(checked) => setFormData({...formData, require_photo: checked})}
                    data-testid="require-photo-checkbox"
                  />
                  <label
                    htmlFor="require_photo"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    📷 Richiedi foto intestatario (obbligatoria)
                  </label>
                </div>
              </div>

              {/* Immagine Personalizzata Biglietto */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="font-semibold text-lg mb-4">🖼️ Immagine Personalizzata Biglietto</h3>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-900">
                    <strong>Aggiungi un'immagine al biglietto:</strong> Logo evento, sponsor, decorazioni. Massimo 5MB, tutti i formati supportati.
                  </p>
                </div>

                {!imagePreview ? (
                  <div className="space-y-4">
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 cursor-pointer transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        Clicca per caricare immagine
                      </p>
                      <p className="text-xs text-gray-600">
                        JPG, PNG, GIF, WebP, SVG - Max 5MB
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden">
                      <img 
                        src={imagePreview} 
                        alt="Anteprima immagine biglietto" 
                        className="w-full h-48 object-contain bg-gray-50"
                        style={{ opacity: formData.custom_image_opacity / 100 }}
                      />
                      <Button
                        type="button"
                        onClick={clearImage}
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    <div>
                      <Label>Posizionamento</Label>
                      <Select 
                        value={formData.custom_image_position} 
                        onValueChange={(value) => setFormData({...formData, custom_image_position: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top-left">Angolo Alto Sinistra</SelectItem>
                          <SelectItem value="top-right">Angolo Alto Destra</SelectItem>
                          <SelectItem value="watermark">Filigrana (Centro)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Trasparenza: {formData.custom_image_opacity}%</Label>
                      <Slider
                        value={[formData.custom_image_opacity]}
                        onValueChange={(value) => setFormData({...formData, custom_image_opacity: value[0]})}
                        min={0}
                        max={100}
                        step={5}
                        className="mt-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        0% = invisibile, 100% = completamente visibile
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Dialog */}
              <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" className="w-full mb-4">
                    <Eye className="w-4 h-4 mr-2" />
                    Anteprima Biglietto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Anteprima Biglietto</DialogTitle>
                    <DialogDescription>
                      Ecco come apparirà il biglietto con le tue personalizzazioni
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <TicketPreview 
                      ticketType={{
                        name: formData.name || 'Nome Biglietto',
                        description: formData.description || 'Descrizione',
                        price: parseFloat(formData.price) || 0,
                        qr_color: formData.qr_color,
                        qr_bg_color: formData.qr_bg_color
                      }}
                      customImage={imagePreview}
                      imagePosition={formData.custom_image_position}
                      imageOpacity={formData.custom_image_opacity}
                      tenant={tenant}
                    />
                  </div>
                </DialogContent>
              </Dialog>

              <div className="flex gap-2">
                <Button type="submit" data-testid="save-ticket-type-button">
                  {editingId ? 'Aggiorna' : 'Salva'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Annulla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Duplicate Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impossibile Modificare</DialogTitle>
            <DialogDescription>
              Questa tipologia di biglietto ha già biglietti venduti o assegnati e non può essere modificata direttamente.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Vuoi creare una <strong>copia modificabile</strong> di questa tipologia? La copia avrà le stesse caratteristiche ma potrà essere modificata liberamente.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                ⚠️ I biglietti già venduti/assegnati rimarranno legati alla tipologia originale.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleDuplicate}>
              <Copy className="w-4 h-4 mr-2" />
              Crea Copia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ticketTypes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Plus className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Nessuna tipologia ancora</h3>
              <p className="text-gray-600 mb-6">
                Inizia creando le tue tipologie di biglietto personalizzate. Puoi creare quante tipologie vuoi con nomi e caratteristiche diverse!
              </p>
              <div className="text-left bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-gray-900 mb-2">Esempi di tipologie:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Standard</strong> - Ingresso base (€20)</li>
                  <li>• <strong>VIP</strong> - Accesso backstage + ristoro (€50)</li>
                  <li>• <strong>Premium</strong> - Tutti gli accessi + merchandising (€80)</li>
                  <li>• <strong>Student</strong> - Sconto studenti (€15)</li>
                  <li>• <strong>Early Bird</strong> - Prevendita anticipata (€18)</li>
                </ul>
              </div>
              <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-first-ticket-type">
                <Plus className="w-4 h-4 mr-2" />
                Crea la Prima Tipologia
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {ticketTypes.map(tt => (
            <Card key={tt.id} data-testid={`ticket-type-${tt.id}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{tt.name}</h3>
                    <p className="text-gray-600 mb-4">{tt.description}</p>
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-gray-600">Prezzo: </span>
                        <span className="font-semibold text-blue-600">€{tt.price.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Venduti: </span>
                        <span className="font-semibold">{tt.quantity_sold}/{tt.quantity_available}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(tt)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`edit-ticket-type-${tt.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(tt.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-ticket-type-${tt.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
